import _defaults from 'lodash/defaults';
import _get from 'lodash/get';
import _head from 'lodash/head';
import _isEmpty from 'lodash/isEmpty';
import _isNumber from 'lodash/isNumber';

import { hasAnyRecipe } from 'components/modules/foundation/hooks/use-recipe';
import { SUPPORTED_INTERACTIONS_IN_BANNER_RECIPES } from 'constants/recipes';

import { objectToCamelCase } from '../../../../../libs/format';
import {
  MARKDOWN_CONTENT_TYPE,
  TEXT_CONTENT_TYPE,
} from '../../../component-configuration/constants/content';
import { createContentComponentConfiguration } from '../../../component-configuration/services/content-node-configuration';
import { ensureInteractions } from '../../../website-component-interaction/components/WebsiteComponentInteractionContainer/WebsiteComponentInteractionContainer.utils';
import {
  ANNOUNCEMENT_FAST_ID,
  CUSTOM_FORM_FAST_ID,
  INITIAL_ANNOUNCEMENT_FORM_STATE,
} from '../../constants';
import {
  ANNOUNCEMENT_DEFAULT_FORM_FIELDS_MAP,
  ANNOUNCEMENT_FORM_FIELDS_DEFAULT_SETTINGS,
} from '../AnnouncementFormFieldsSettings/AnnouncementFormFieldsSettings.constants';
import { DEFAULT_DELAY_IN_MILLISECONDS } from '../AnnouncementTiming/AnnouncementTiming.constants';

export function getFormFieldSettings(fields) {
  const defaultSettings = ANNOUNCEMENT_FORM_FIELDS_DEFAULT_SETTINGS;
  const updatedSettings = defaultSettings.map((setting) => {
    const field = fields.find((x) => x.name === setting.name);
    return {
      ...setting,
      enabled: field ? true : setting.enabled,
    };
  });

  return updatedSettings;
}

export function getAnnouncementFormFields(isFormEnabled, fields) {
  const formFields = [];

  if (!isFormEnabled) {
    return [];
  }

  fields.forEach((field) => {
    const { name, enabled } = field;
    if (enabled) {
      formFields.push(ANNOUNCEMENT_DEFAULT_FORM_FIELDS_MAP[name]);
    }
  });

  return formFields;
}

export function customFormConfigurationToAnnouncementForm(customFormConfiguration) {
  const { data: { submitButtonText = '', subscriptionTags = [], fields = [] } = {} } =
    customFormConfiguration || {};

  const formEnabled = !_isEmpty(fields);

  const formFieldsSettings = getFormFieldSettings(fields);

  return {
    formEnabled,
    submitButtonText,
    subscriptionTags,
    formFieldsSettings,
  };
}

export function announcementFormToCustomFormConfiguration(
  announcementForm,
  announcementComponentConfigurationId,
  customFormComponentConfiguration = {},
) {
  const {
    formEnabled = false,
    submitButtonText = '',
    subscriptionTags = [],
    formFieldsSettings,
  } = announcementForm;

  const {
    id = null,
    order = 0,
    fastId = CUSTOM_FORM_FAST_ID,
    layout = null,
  } = customFormComponentConfiguration;

  const data = {
    submitButtonText,
    subscriptionTags,
    fields: getAnnouncementFormFields(formEnabled, formFieldsSettings),
  };

  const newCustomFormComponentConfiguration = {
    id,
    order,
    fastId,
    layout,
    parentComponentConfigurationId: announcementComponentConfigurationId,
    data,
    files: [],
    interactions: [],
    contentNodes: [],
    enabled: formEnabled,
  };

  return newCustomFormComponentConfiguration;
}

export function createAnnouncementForm(initialAnnouncementForm) {
  const defaultCustomFormConfigurationData = customFormConfigurationToAnnouncementForm({});

  const formData = _defaults(initialAnnouncementForm, INITIAL_ANNOUNCEMENT_FORM_STATE, {
    title: '',
    images: [],
    content: '',
    websiteComponentInteractions: objectToCamelCase(ensureInteractions([])),
    ...defaultCustomFormConfigurationData,
  });

  const {
    startTime,
    endTime,
    referenceId,
    bannerEnabled,
    bannerBackgroundColor,
    bannerButtonText,
    bannerPosition,
    title,
    popupEnabled,
    dismissalType,
    delay,
    duration,
    imagePosition,
    formEnabled,
    submitButtonText,
    subscriptionTags,
    images,
    content,
    websiteComponentInteractions,
    formFieldsSettings,
  } = formData;

  return {
    startTime,
    endTime,
    referenceId,
    bannerEnabled,
    bannerBackgroundColor,
    bannerButtonText,
    bannerPosition,
    title,
    popupEnabled,
    dismissalType,
    delay,
    duration,
    imagePosition,
    formEnabled,
    submitButtonText,
    subscriptionTags,
    images,
    content,
    websiteComponentInteractions,
    formFieldsSettings,
  };
}

export function getCustomFormComponentConfiguration(announcementComponentConfiguration) {
  const { childConfigurations = [] } = announcementComponentConfiguration;
  const customFormConfiguration = childConfigurations.find(
    (configuration) => configuration.fastId === CUSTOM_FORM_FAST_ID,
  );
  return customFormConfiguration || {};
}

export function announcementConfigurationToForm(announcementComponentConfiguration) {
  if (!announcementComponentConfiguration) {
    return {};
  }

  const announcementComponentConfigurationId = _get(announcementComponentConfiguration, 'id');

  const {
    data: {
      startTime,
      endTime,
      referenceId,
      bannerEnabled,
      bannerBackgroundColor,
      bannerButtonText,
      bannerPosition,
      popupEnabled,
      dismissalType,
      delay,
      duration,
      imagePosition,
    } = {},
    files = [],
    interactions = [],
    contentNodes = [],
  } = announcementComponentConfiguration;

  const bannerContent = _get(
    contentNodes.filter(({ contentType }) => contentType === TEXT_CONTENT_TYPE),
    '[0].content',
    '',
  );

  const popupContent = _get(
    contentNodes.filter(({ contentType }) => contentType === MARKDOWN_CONTENT_TYPE),
    '[0].content',
    '',
  );

  const websiteInteractions = objectToCamelCase(ensureInteractions(interactions));

  const images = files.map(({ id, order, metadata }) => ({ id, order, metadata }));

  const customFormConfiguration = getCustomFormComponentConfiguration(
    announcementComponentConfiguration,
  );

  const customFormConfigurationData =
    customFormConfigurationToAnnouncementForm(customFormConfiguration);

  return {
    startTime,
    endTime,
    referenceId,
    bannerEnabled,
    bannerBackgroundColor,
    bannerButtonText,
    bannerPosition,
    title: bannerContent,
    popupEnabled,
    dismissalType,
    delay: _isNumber(announcementComponentConfigurationId) ? delay : DEFAULT_DELAY_IN_MILLISECONDS,
    duration,
    imagePosition,
    images,
    content: popupContent,
    websiteComponentInteractions: websiteInteractions,
    ...customFormConfigurationData,
  };
}

export function legacyConfigurationToForm(legacyComponentConfiguration) {
  const data = _get(legacyComponentConfiguration, 'data', {});

  const bannerBackgroundColor = _get(data, 'bannerBackgroundColor', '');
  const content = _get(data, 'content', '');
  const endTime = _get(data, 'endTime', '');
  const modalDelay = _get(data, 'modalDelay', DEFAULT_DELAY_IN_MILLISECONDS);
  const startTime = _get(data, 'startTime', '');
  const subTitle = _get(data, 'subTitle', '');
  const subject = _get(data, 'subject', '');

  const bannerEnabled = !_isEmpty(subject);
  const bannerButtonText = 'See more';

  let popupContent = '';
  if (!_isEmpty(subject)) {
    popupContent += `## ${subject}\n\n`;
  }
  if (!_isEmpty(subTitle)) {
    popupContent += `### ${subTitle}\n\n`;
  }
  if (!_isEmpty(content)) {
    popupContent += content;
  }
  const popupEnabled = !_isEmpty(popupContent);

  return {
    startTime,
    endTime,
    delay: _isNumber(modalDelay) ? modalDelay : DEFAULT_DELAY_IN_MILLISECONDS,

    title: subject,
    bannerEnabled,
    bannerBackgroundColor,
    bannerButtonText,

    popupEnabled,
    content: popupContent,
  };
}

export function announcementFormToConfiguration(
  announcementForm,
  announcementComponentConfiguration = {},
) {
  const {
    startTime,
    endTime,
    referenceId,
    bannerEnabled,
    bannerBackgroundColor,
    bannerButtonText,
    bannerPosition,
    title,
    popupEnabled,
    dismissalType,
    delay,
    duration,
    imagePosition,
    images,
    content,
    websiteComponentInteractions,
  } = announcementForm;

  const {
    contentNodes = [],
    id = null,
    order = 0,
    fastId = ANNOUNCEMENT_FAST_ID,
    layout = null,
  } = announcementComponentConfiguration;

  const bannerContentConfiguration = _head(
    contentNodes.filter(({ contentType }) => contentType === TEXT_CONTENT_TYPE),
    {},
  );
  const newBannerContentConfiguration = createContentComponentConfiguration({
    ...bannerContentConfiguration,
    contentName: 'banner',
    contentType: TEXT_CONTENT_TYPE,
    content: title,
  });
  const popupContentConfiguration = _head(
    contentNodes.filter(({ contentType }) => contentType === MARKDOWN_CONTENT_TYPE),
    {},
  );
  const newPopupContentConfiguration = createContentComponentConfiguration({
    ...popupContentConfiguration,
    contentName: 'popup',
    contentType: MARKDOWN_CONTENT_TYPE,
    content,
  });

  const enabled = popupEnabled || bannerEnabled;

  const newAnnouncementComponentConfiguration = {
    id,
    order,
    fastId,
    layout,
    data: {
      startTime,
      endTime,
      referenceId,
      bannerEnabled,
      bannerBackgroundColor,
      bannerButtonText,
      bannerPosition,
      popupEnabled,
      dismissalType,
      delay,
      duration,
      imagePosition,
    },
    files: images,
    interactions: websiteComponentInteractions,
    contentNodes: [newBannerContentConfiguration, newPopupContentConfiguration],
    enabled,
  };

  return newAnnouncementComponentConfiguration;
}

export function doesRecipeSupportsInteractionsInBanner(patch) {
  return hasAnyRecipe(patch, SUPPORTED_INTERACTIONS_IN_BANNER_RECIPES);
}
